// Ancien abonnement retiré
import { SepaGuard } from '@/components/guards/SepaGuard';
import { useGlobalFolders } from '@/hooks/useDomains';
import { domainsService } from '@/services/domains.service';
import { useDomainsStore } from '@/store/domains.store';
import { Folder } from '@/types/domain.types';
import {
  Check,
  ChevronDown,
  Columns3,
  FileText,
  FolderOpen,
  Inbox,
  Mail,
  Server,
  Settings,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AutomationDrawer,
  BulkAutomationDrawer,
  BulkEditionsDrawer,
  BulkSenderDrawer,
  FolderItem,
  GlobalFoldersDomainNameFilter,
  GlobalFoldersEmptyState,
  GlobalFoldersPagination,
  GlobalFoldersResponsableSelect,
  GlobalFoldersScopeToggle,
  GlobalFoldersPageSizeSelector,
  GlobalFoldersSearchBar,
  GlobalFoldersSortDropdown,
  GlobalFoldersStatusFilter,
} from '../components';
import { collecterDossiersNommesDuFiltre } from '../utils/attribuer-expediteur';
import { VueCollecte } from '@/features/demande-variables/components/VueCollecte';
import { cn } from '@/utils/cn';
import { useOrganisationsStore } from '@/store/organisations.store';
import { useCurrentMemberRole } from '@/hooks/useCurrentMemberRole';
import { PanneauCouvertureSaisie } from '@/features/demande-variables/components/PanneauCouvertureSaisie';

export const GlobalFoldersPage = () => {
  const { t } = useTranslation();
  const {
    clearGlobalFolders,
    selectedFolders,
    toggleFolderSelection,
    selectAllFolders,
    clearFolderSelection,
    isSelected,
    globalFoldersStatusFilter,
    globalFoldersDomainNameFilter,
    globalFoldersResponsableFilter,
    globalFoldersLimit,
    globalFoldersSortBy,
    globalFoldersSortOrder,
    globalFoldersSearchQuery,
    getGlobalFoldersQueryParams,
  } = useDomainsStore();

  const { currentOrganisation } = useOrganisationsStore();
  const { role: roleDansLeCabinet } = useCurrentMemberRole();
  // Même règle que l'API : un lecteur regarde, il ne règle rien.
  const peutAgirSurLaCollecte =
    !currentOrganisation || roleDansLeCabinet !== 'viewer';
  const [isBulkSenderDrawerOpen, setIsBulkSenderDrawerOpen] = useState(false);

  // Mêmes filtres que la liste affichée, toutes les pages (100 au plus par
  // page côté API).
  const loadAllFilteredFolders = () =>
    collecterDossiersNommesDuFiltre(page =>
      domainsService.getAllFolders({
        ...getGlobalFoldersQueryParams(),
        page,
        limit: 100,
      })
    );

  // Ancien abonnement retiré

  // Automation drawer state
  const [isAutomationDrawerOpen, setIsAutomationDrawerOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);

  // Bulk automation drawer state
  const [isBulkAutomationDrawerOpen, setIsBulkAutomationDrawerOpen] =
    useState(false);

  // Subscription modal state
  // Ancien modal d'abonnement retiré

  // Bulk editions drawer state
  const [isBulkEditionsDrawerOpen, setIsBulkEditionsDrawerOpen] =
    useState(false);
  const [bulkEditionsFolderUuids, setBulkEditionsFolderUuids] = useState<
    string[]
  >([]);
  const [bulkEditionsFolderNames, setBulkEditionsFolderNames] = useState<
    string[]
  >([]);

  // Bulk selection mode
  const [isBulkMode, setIsBulkMode] = useState(false);
  // « Dossiers » (la liste) ou « Collecte du mois » (ce que chaque client a
  // envoyé pour ses variables) — même page, même recherche, comme dans HUP.
  const [vue, setVue] = useState<'dossiers' | 'collecte'>('dossiers');
  /*
    Le masque de saisie des clients, vu sur TOUS les dossiers : lesquels ont
    leurs colonnes prévues, lesquels non. Décision Rose du 22/09/2026 ; sur la
    liste, jamais une entrée de menu de plus (règle 12G) — et le bouton porte
    le nom du geste, « Colonnes de saisie », parce que « Paramètres » désigne
    déjà l'écran du compte dans le menu.
  */
  const [parametresOuverts, setParametresOuverts] = useState(false);
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);

  const activeFiltersCount = [
    globalFoldersStatusFilter !== 'all',
    globalFoldersDomainNameFilter.trim().length > 0,
    globalFoldersResponsableFilter.trim().length > 0,
    globalFoldersLimit !== 20,
    globalFoldersSortBy !== 'name' || globalFoldersSortOrder !== 'ASC',
  ].filter(Boolean).length;

  useEffect(() => {
    if (activeFiltersCount > 0) {
      setAreFiltersOpen(true);
    }
  }, [activeFiltersCount]);

  const handleAutomationClick = (folder: Folder) => {
    setSelectedFolder(folder);
    setIsAutomationDrawerOpen(true);
  };

  const handleDataRefresh = () => {
    refetchFolders();
  };

  const handleCloseAutomationDrawer = () => {
    setIsAutomationDrawerOpen(false);
    setSelectedFolder(null);
  };

  const handleBulkAutomationClick = () => {
    setIsBulkAutomationDrawerOpen(true);
  };

  const handleCloseBulkAutomationDrawer = () => {
    setIsBulkAutomationDrawerOpen(false);
  };

  const handleToggleBulkMode = () => {
    setIsBulkMode(!isBulkMode);
    if (isBulkMode) {
      clearFolderSelection();
    }
  };

  const handleSelectAll = () => {
    if (selectedFolders.size === folders.length) {
      clearFolderSelection();
    } else {
      selectAllFolders(folders.map(folder => folder.uuid));
    }
  };

  const {
    folders,
    isLoading: isFoldersLoading,
    error: foldersError,
    pagination,
    refetch: refetchFolders,
  } = useGlobalFolders();

  // Get selected folders objects
  const selectedFoldersObjects = useMemo(() => {
    return folders.filter(folder => selectedFolders.has(folder.uuid));
  }, [folders, selectedFolders]);

  // Clear global folders when leaving the page
  useEffect(() => {
    return () => {
      clearGlobalFolders();
      clearFolderSelection();
    };
  }, [clearGlobalFolders, clearFolderSelection]);

  if (isFoldersLoading && folders.length === 0) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="animate-pulse">
          <div className="mb-6 h-24 rounded-2xl bg-neutral-200 sm:mb-8 sm:h-32"></div>
          <div className="space-y-3 sm:space-y-4">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="h-12 rounded-lg bg-neutral-200 sm:h-16"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="rounded-xl border border-neutral-200 bg-white p-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="bg-primary-100 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl">
            <FolderOpen className="text-primary-600 h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-neutral-900">
              {t('domains.folders.global.title', 'All Folders')}
            </h1>
            <p className="mt-0.5 text-sm text-neutral-500">
              {t('domains.folders.global.accessibleCount', {
                count: pagination.total,
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Folders Section */}
      <div className="space-y-4">
        {/* sm:flex-wrap : quand les actions groupées ne tiennent plus à côté
            du sélecteur, elles passent dessous au lieu de le chevaucher. */}
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {/* Toggle « Mes dossiers » / « Tous les dossiers » (si organisation) */}
            <GlobalFoldersScopeToggle />
            <div
              role="group"
              aria-label="Vue"
              className="inline-flex flex-shrink-0 items-center rounded-lg border border-neutral-300 bg-white p-0.5"
            >
              {(
                [
                  { value: 'dossiers', label: 'Dossiers', icon: FolderOpen },
                  { value: 'collecte', label: 'Collecte du mois', icon: Inbox },
                ] as const
              ).map(option => {
                const Icon = option.icon;
                const active = vue === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setVue(option.value)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      active
                        ? 'bg-primary-600 text-white'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bulk Actions */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Bulk editions - selected folders */}
            {isBulkMode && selectedFolders.size > 0 && (
              <SepaGuard>
                <button
                  onClick={() => {
                    const selected = folders.filter(f =>
                      selectedFolders.has(f.uuid)
                    );
                    setBulkEditionsFolderUuids(selected.map(f => f.uuid));
                    setBulkEditionsFolderNames(selected.map(f => f.name));
                    setIsBulkEditionsDrawerOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
                >
                  <FileText className="h-4 w-4" />
                  {t(
                    'editions.bulk.generateSelected',
                    'Générer éditions ({{count}})',
                    {
                      count: selectedFolders.size,
                    }
                  )}
                </button>
              </SepaGuard>
            )}

            {isBulkMode && selectedFolders.size > 0 && (
              <button
                onClick={() => setIsBulkSenderDrawerOpen(true)}
                className="flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
              >
                <Mail className="h-4 w-4" />
                Expéditeur ({selectedFolders.size})
              </button>
            )}

            {isBulkMode && selectedFolders.size > 0 && (
              <SepaGuard>
                <button
                  onClick={handleBulkAutomationClick}
                  className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
                >
                  <Settings className="h-4 w-4" />
                  {t(
                    'automation.bulk.configure',
                    'Configure {{count}} folders',
                    {
                      count: selectedFolders.size,
                    }
                  )}
                </button>
              </SepaGuard>
            )}

            {vue === 'dossiers' && peutAgirSurLaCollecte && (
              <button
                type="button"
                onClick={() => setParametresOuverts(true)}
                className="flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
              >
                <Columns3 className="h-4 w-4" />
                Colonnes de saisie
              </button>
            )}

            <button
              onClick={handleToggleBulkMode}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                isBulkMode
                  ? 'bg-neutral-700 text-white hover:bg-neutral-800'
                  : 'border border-neutral-300 text-neutral-700 hover:bg-neutral-50'
              }`}
            >
              {isBulkMode ? (
                <>
                  <X className="h-4 w-4" />
                  {t('common.cancel', 'Cancel')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {t('domains.folders.bulkMode', 'Select')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Recherche principale : les réglages occasionnels restent disponibles
            sans monopoliser l'écran à chaque visite. */}
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="min-w-0 flex-1">
            <GlobalFoldersSearchBar />
          </div>
          {vue === 'dossiers' && (
            <button
              type="button"
              aria-expanded={areFiltersOpen}
              aria-controls="global-folders-filters"
              onClick={() => setAreFiltersOpen(open => !open)}
              className="flex items-center justify-center gap-2 rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {t('domains.folders.filtersButton', 'Filters')}
              {activeFiltersCount > 0 && (
                <span className="bg-primary-100 text-primary-700 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs">
                  {activeFiltersCount}
                </span>
              )}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${areFiltersOpen ? 'rotate-180' : ''}`}
              />
            </button>
          )}
          {isBulkMode && (
            <button
              onClick={handleSelectAll}
              className="flex items-center justify-center gap-2 rounded-lg border border-neutral-300 px-3 py-2.5 text-sm transition-colors hover:bg-neutral-50"
            >
              {selectedFolders.size === folders.length ? (
                <>
                  <X className="h-4 w-4" />
                  {t('common.deselectAll', 'Deselect All')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {t('common.selectAll', 'Select All')}
                </>
              )}
            </button>
          )}
        </div>

        {vue === 'dossiers' && areFiltersOpen && (
          <div
            id="global-folders-filters"
            className="grid grid-cols-1 gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-3 sm:grid-cols-2 lg:grid-cols-5"
          >
            <GlobalFoldersStatusFilter />
            <GlobalFoldersDomainNameFilter />
            <GlobalFoldersResponsableSelect />
            <GlobalFoldersPageSizeSelector />
            <GlobalFoldersSortDropdown />
          </div>
        )}

        {/* Résultat courant (la vue Collecte porte son propre compte) */}
        {vue === 'dossiers' && (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-neutral-600">
              {isFoldersLoading ? (
                <span>{t('common.loading', 'Loading...')}</span>
              ) : (
                <span>
                  {t(
                    'domains.folders.global.resultsCount',
                    'Found {{count}} folders',
                    {
                      count: pagination.total,
                    }
                  )}
                  {isBulkMode && selectedFolders.size > 0 && (
                    <span className="text-primary-600 ml-2">
                      ({selectedFolders.size} {t('common.selected', 'selected')}
                      )
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Folders List */}
        {vue === 'collecte' ? (
          <VueCollecte
            recherche={globalFoldersSearchQuery}
            peutAgir={peutAgirSurLaCollecte}
          />
        ) : isFoldersLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-lg bg-neutral-200 sm:h-16"
              ></div>
            ))}
          </div>
        ) : foldersError ? (
          <div className="bg-error-50 rounded-xl border border-red-200 p-6 text-center sm:p-8">
            <div className="bg-error-100 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
              <Server className="text-error-600 h-6 w-6" />
            </div>
            <h3 className="text-error-900 mb-2 font-semibold">
              {t(
                'domains.folders.global.error.title',
                'Failed to load folders'
              )}
            </h3>
            <p className="text-error-700 text-sm sm:text-base">
              {typeof foldersError === 'string'
                ? foldersError
                : t(
                    'domains.folders.global.error.description',
                    'Please try again later'
                  )}
            </p>
          </div>
        ) : folders.length === 0 ? (
          <GlobalFoldersEmptyState />
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {folders.map(folder => (
              <FolderItem
                key={folder.uuid}
                folder={folder}
                onAutomationClick={handleAutomationClick}
                configurationGuard={SepaGuard}
                showDomain={true} // Show domain info in global view
                showCheckbox={isBulkMode}
                isSelected={isSelected(folder.uuid)}
                onToggleSelection={toggleFolderSelection}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {folders.length > 0 && <GlobalFoldersPagination />}
      </div>

      {/* Automation Drawer */}
      <AutomationDrawer
        isOpen={isAutomationDrawerOpen}
        onClose={handleCloseAutomationDrawer}
        folder={selectedFolder}
        onDataRefresh={handleDataRefresh}
      />

      {/* Bulk Automation Drawer */}
      <BulkAutomationDrawer
        isOpen={isBulkAutomationDrawerOpen}
        onClose={handleCloseBulkAutomationDrawer}
        selectedFolders={selectedFoldersObjects}
        onDataRefresh={handleDataRefresh}
      />

      {/* Bulk Editions Drawer */}
      <BulkEditionsDrawer
        isOpen={isBulkEditionsDrawerOpen}
        onClose={() => setIsBulkEditionsDrawerOpen(false)}
        folderUuids={bulkEditionsFolderUuids}
        folderNames={bulkEditionsFolderNames}
      />

      <PanneauCouvertureSaisie
        ouvert={parametresOuverts}
        onFermer={() => setParametresOuverts(false)}
      />

      <BulkSenderDrawer
        isOpen={isBulkSenderDrawerOpen}
        onClose={() => setIsBulkSenderDrawerOpen(false)}
        selectedFolders={[...selectedFolders].map(uuid => ({
          uuid,
          // Un dossier coché sur une autre page n'a pas son nom sous la main :
          // il sera désigné par son identifiant en cas d'échec.
          name: folders.find(folder => folder.uuid === uuid)?.name,
        }))}
        filterTotal={pagination.total}
        loadAllFilteredFolders={loadAllFilteredFolders}
      />

      {/* Ancien modal d'abonnement retiré */}
    </div>
  );
};
